/**
 * Table de routes COMPLÈTE — URLs identiques au Nuxt (spec §8.2).
 *
 * S1 est seul propriétaire de ce fichier : aucun lot n'y touche. Les pages dont
 * le lot n'est pas encore écrit pointent sur une souche au chemin EXACT de la
 * table (§8.5), que le lot propriétaire écrase intégralement.
 *
 * ⚠️ `/garde` n'est PAS porté (décision verrouillée) : pas de route, pas de
 * placeholder, pas d'entrée de navigation.
 */
import type { RouteRecordRaw } from 'vue-router';
import { AppRole } from '@/shared/enums';
import { clientPreviewRedirect } from './redirects';

declare module 'vue-router' {
  interface RouteMeta {
    /** Rôles autorisés. Absent = tout utilisateur authentifié PORTANT un rôle. */
    roles?: readonly AppRole[];
    /** 'app' (défaut) | 'minimal'. */
    layout?: 'app' | 'minimal';
    /** Titre de page, SANS le suffixe. document.title = `${title} — Hello`. */
    title?: string;
    /** true = aucune authentification requise (aucune route en session 1). */
    public?: true;
    /** true = accessible même sans rôle applicatif (uniquement /forbidden). */
    allowNoRole?: true;
  }
}

const INTERNAL = [AppRole.ADMIN, AppRole.AMESURA] as const;
const ADMIN_ONLY = [AppRole.ADMIN] as const;
const CLIENT_ONLY = [AppRole.CLIENT] as const;

export const routes: RouteRecordRaw[] = [
  {
    // ⚠️ Jamais rendu : le guard redirige par rôle en beforeEach. Le composant
    // est là parce que vue-router 4 exige component/children/redirect sur chaque
    // record — et la cible dépend du rôle, donc pas de redirect statique.
    path: '/',
    name: 'home',
    component: () => import('@/features/system/pages/ComingSoonPage.vue'),
    meta: { layout: 'minimal' },
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/features/system/pages/ForbiddenPage.vue'),
    meta: { layout: 'minimal', title: 'Accès refusé', allowNoRole: true },
  },

  // ── Tickets (session 2) ───────────────────────────────────────────────────
  {
    path: '/issues',
    name: 'client-issues',
    component: () => import('@/features/issues/pages/ClientIssuesPage.vue'),
    meta: { roles: CLIENT_ONLY, layout: 'app', title: 'Tickets' },
  },
  {
    path: '/tickets',
    name: 'issues',
    component: () => import('@/features/issues/pages/InternalIssuesPage.vue'),
    meta: { roles: INTERNAL, layout: 'app', title: 'Tickets' },
  },
  {
    path: '/t/:identifier',
    name: 'issue-short-link',
    component: () => import('@/features/issues/pages/IssueShortLinkPage.vue'),
    meta: { layout: 'app', title: 'Ticket' },
  },

  // ── Anciennes pages clients : redirections vers l'aperçu de /tickets (QP1) ──
  // Pas de `meta` : le guard voit la cible APRÈS redirection.
  {
    path: '/clients',
    name: 'clients',
    redirect: { name: 'issues' },
  },
  {
    path: '/clients/:id',
    name: 'client-board',
    redirect: clientPreviewRedirect,
  },

  // ── Suivi de projet (session 4) ───────────────────────────────────────────
  {
    path: '/suivi-projet',
    name: 'tracker-clients',
    component: () => import('@/features/tracker/pages/TrackerClientsPage.vue'),
    meta: { layout: 'app', title: 'Suivi de projet' },
  },
  {
    path: '/suivi-projet/:clientId',
    name: 'tracker',
    component: () => import('@/features/tracker/pages/TrackerPage.vue'),
    meta: { layout: 'app', title: 'Suivi de projet' },
  },

  // ── Réunions (session 4) ──────────────────────────────────────────────────
  {
    path: '/reunions/nouvelle',
    name: 'meeting-new',
    component: () => import('@/features/tracker/pages/MeetingComposerPage.vue'),
    meta: { roles: INTERNAL, layout: 'app', title: 'Nouveau compte-rendu' },
  },
  {
    path: '/reunions/:id',
    name: 'meeting',
    component: () => import('@/features/tracker/pages/MeetingDetailPage.vue'),
    meta: { layout: 'app', title: 'Compte-rendu' },
  },

  // ── GitLab / Timesheet (session 3) ────────────────────────────────────────
  {
    path: '/gitlab',
    name: 'gitlab',
    component: () => import('@/features/gitlab/pages/GitlabDashboardPage.vue'),
    meta: { roles: INTERNAL, layout: 'app', title: 'Tableau GitLab' },
  },
  {
    path: '/timesheet',
    name: 'timesheet',
    component: () => import('@/features/time/pages/TimesheetPage.vue'),
    meta: { roles: INTERNAL, layout: 'app', title: 'Timesheet' },
  },

  // ── Session 1 ─────────────────────────────────────────────────────────────
  {
    path: '/links',
    name: 'links',
    component: () => import('@/features/links/pages/LinksPage.vue'),
    meta: { layout: 'app', title: 'Liens utiles' },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/features/profile/pages/ProfilePage.vue'),
    meta: { roles: INTERNAL, layout: 'app', title: 'Profil' },
  },
  {
    path: '/backoffice',
    name: 'backoffice-clients',
    component: () => import('@/features/clients/pages/ClientsPage.vue'),
    meta: { roles: ADMIN_ONLY, layout: 'app', title: 'Clients — Back office' },
  },
  {
    path: '/backoffice/clients/:id',
    name: 'backoffice-client',
    component: () => import('@/features/clients/pages/ClientDetailPage.vue'),
    meta: { roles: ADMIN_ONLY, layout: 'app', title: 'Fiche client' },
  },
  {
    path: '/backoffice/clients/:id/client-view',
    name: 'backoffice-client-view',
    redirect: clientPreviewRedirect,
  },
  {
    path: '/backoffice/users',
    name: 'backoffice-users',
    component: () => import('@/features/users/pages/InternalUsersPage.vue'),
    meta: { roles: ADMIN_ONLY, layout: 'app', title: 'Utilisateurs — Back office' },
  },
  {
    path: '/backoffice/links',
    name: 'backoffice-links',
    component: () => import('@/features/links/pages/LinksAdminPage.vue'),
    meta: { roles: ADMIN_ONLY, layout: 'app', title: 'Annuaires — Back office' },
  },
  {
    path: '/backoffice/projects',
    name: 'backoffice-projects',
    component: () => import('@/features/catalog/pages/ProjectsPage.vue'),
    meta: { roles: ADMIN_ONLY, layout: 'app', title: 'Projets & budgets' },
  },
  {
    path: '/backoffice/temps',
    name: 'backoffice-time',
    component: () => import('@/features/time/pages/TeamTimePage.vue'),
    meta: { roles: ADMIN_ONLY, layout: 'app', title: 'Temps équipe' },
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/features/system/pages/NotFoundPage.vue'),
    meta: { layout: 'minimal', title: 'Page introuvable' },
  },
];
